import { useEffect, useMemo, useRef } from "react";

import {
  CONNECTION_STEPS,
  type ConnectionState,
  type ConnectionStep,
} from "../lib/connection-state";
import styles from "./ConnectionSplash.module.scss";

type StepState = "pending" | "active" | "done" | "error";

interface Step {
  key: ConnectionStep;
  label: string;
  state: StepState;
}

export interface ConnectionSplashProps {
  /** Room/session id, shown as the card heading. Omit when the caller already renders
   * it elsewhere (e.g. ConsolePage's per-panel header) to avoid showing it twice. */
  roomId?: string;
  /** Where the primary connect sequence currently is — see `lib/connection-state.ts`. */
  connectionState: ConnectionState;
  /** Text for a `connectionState.kind === "failed" && step === "config"` error. */
  configError?: string | null;
  /** Current fine-grained status line, already localized (the caller maps `session`'s
   * raw status code via `statusCodeToLabel`) — shown for every other failed step, and
   * as the live diagnostic line while still pending. */
  statusText: string;
  onReconnect?: () => void;
  /** Raw connection/diagnostics log lines (already formatted, e.g. `useOperatorLogs().system`). */
  logLines?: string[];
  /** Plays the fade-out exit animation instead of unmounting instantly — set while the
   * caller keeps this splash mounted for one more frame during the handoff to its ready
   * interface (see `useReadyTransition`). */
  leaving?: boolean;
}

const STEP_LABELS: Record<ConnectionStep, string> = {
  config: "Загрузка конфигурации приложения",
  connect: "Установка сессии",
  robotConfig: "Получение конфигурации робота",
  video: "Активация видео и команд",
};

function stepIcon(state: StepState): string | null {
  switch (state) {
    case "done":
      return "bi-check-lg";
    case "error":
      return "bi-exclamation";
    default:
      return null;
  }
}

function useSteps(connectionState: ConnectionState): Step[] {
  return useMemo(() => {
    const currentIndex =
      connectionState.kind === "active"
        ? CONNECTION_STEPS.length
        : CONNECTION_STEPS.indexOf(connectionState.step);

    return CONNECTION_STEPS.map((step, index) => {
      let state: StepState;
      if (index < currentIndex) state = "done";
      else if (index > currentIndex) state = "pending";
      else state = connectionState.kind === "failed" ? "error" : "active";
      return { key: step, label: STEP_LABELS[step], state };
    });
  }, [connectionState]);
}

/**
 * Per-session connection splash — shown INSIDE one operator's own render tree (a
 * ConsolePage panel, or the whole ViewerPage while nothing else is ready) while that
 * instance's own stack (config → signaling/WebRTC → robot-config → video) comes up.
 * Each session owns its own hook results, so each gets an independent splash — nothing
 * here is shared/global state.
 */
export function ConnectionSplash({
  roomId,
  connectionState,
  configError,
  statusText,
  onReconnect,
  logLines = [],
  leaving = false,
}: ConnectionSplashProps) {
  const steps = useSteps(connectionState);
  const failed = connectionState.kind === "failed";

  const logRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logLines]);

  return (
    <div
      className={`${styles.root}${leaving ? ` ${styles.leaving}` : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className={styles.inner}>
        <div className={styles.content}>
          <div className={styles.header}>
            <span
              className={`${styles.badge}${failed ? ` ${styles.error}` : ""}`}
              aria-hidden="true"
            >
              <i className={`bi ${failed ? "bi-exclamation-triangle-fill" : "bi-broadcast"}`} />
            </span>
            <div className={styles.heading}>
              <span className={styles.eyebrow}>
                {failed ? "Ошибка подключения" : "Подключение к роботу"}
              </span>
              {roomId ? <span className={styles.room}>{roomId}</span> : null}
            </div>
          </div>

          <ol className={styles.steps}>
            {steps.map((step) => {
              const icon = stepIcon(step.state);
              return (
                <li key={step.key} className={`${styles.step} ${styles[step.state] ?? ""}`}>
                  <span className={styles.dot} aria-hidden="true">
                    {icon ? <i className={`bi ${icon}`} /> : null}
                  </span>
                  <span className={styles.stepLabel}>{step.label}</span>
                </li>
              );
            })}
          </ol>

          {failed && onReconnect ? (
            <button type="button" className={styles.retry} onClick={onReconnect}>
              <i className="bi bi-arrow-repeat" aria-hidden="true" /> Переподключить
            </button>
          ) : null}
        </div>

        <div className={styles.log} ref={logRef}>
          {logLines.length > 0 ? (
            logLines.map((line, i) => (
              <div key={i} className={styles.logLine}>
                {line}
              </div>
            ))
          ) : (
            <div className={`${styles.logLine} ${styles.muted}`}>
              {connectionState.kind === "failed" && connectionState.step === "config"
                ? configError
                : statusText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
